<template>
  <v-card min-height="600px">
    <v-card-title class="primary white--text">
      <span>
        {{ $t('createExportBatch') }}
      </span>
      <v-spacer/>
      <BaseDialogActions
        @refresh="refreshData()"
      />
    </v-card-title>
    <v-card-text>
      <Loading v-if="isLoading"/>
      <v-container fluid v-else>
        <v-row dense v-show="!this.noData">
          <v-col>
            <v-text-field
            v-model="label"
            :label="$t('label')"
            :rules="labelRules"
            ref="exportBatchLabelTextfield"
            data-testid="export-batch-label"
            color="black"
            counter
            maxlength="64"/>
          </v-col>
        </v-row>
        <v-row>
          <v-expansion-panels popout accordion tile v-model="openPanel" v-if="!this.noData">
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary white--text"
                hide-actions
                :disabled="tickets.length === 0"
                :style="`cursor: ${tickets.length === 0 ? 'default' : 'pointer'};${tickets.length === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('logTickets') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ getTitleString(selectedTickets, tickets) }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ExportBatchSelectionTable
                  :items="tickets"
                  :selectedItems.sync="selectedTickets"
                  :headers="headers.ticketHeaders"
                  tableKey="ticketId"
                  :filterTypes="ticketFiltersForExport(true)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary white--text"
                hide-actions
                :disabled="byproductTickets.length === 0"
                :style="`cursor: ${byproductTickets.length === 0 ? 'default' : 'pointer'};${byproductTickets.length === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('byproductTickets') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ getTitleString(selectedByproductTickets, byproductTickets) }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ExportBatchSelectionTable
                  :items="byproductTickets"
                  :selectedItems.sync="selectedByproductTickets"
                  :headers="headers.byproductTicketHeaders"
                  tableKey="ticketId"
                  :filterTypes="ticketFiltersForExport(false)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary white--text"
                hide-actions
                :disabled="transferTickets.length === 0"
                :style="`cursor: ${transferTickets.length === 0 ? 'default' : 'pointer'};${transferTickets.length === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('transferTickets') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ getTitleString(selectedTransferTickets, transferTickets) }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ExportBatchSelectionTable
                  :items="transferTickets"
                  :selectedItems.sync="selectedTransferTickets"
                  :headers="headers.byproductTicketHeaders"
                  tableKey="ticketId"
                  :filterTypes="ticketFiltersForExport(false)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary white--text"
                hide-actions
                :disabled="logYardSaleTickets.length === 0"
                :style="`cursor: ${logYardSaleTickets.length === 0 ? 'default' : 'pointer'};${logYardSaleTickets.length === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('logYardSaleTickets') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ getTitleString(selectedLogYardSaleTickets, logYardSaleTickets) }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ExportBatchSelectionTable
                  :items="logYardSaleTickets"
                  :selectedItems.sync="selectedLogYardSaleTickets"
                  :headers="headers.byproductTicketHeaders"
                  tableKey="ticketId"
                  :filterTypes="ticketFiltersForExport(false)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary whtie--text"
                hide-actions
                :disabled="totalCorrections === 0"
                :style="`cursor: ${totalCorrections === 0 ? 'default' : 'pointer'};${totalCorrections === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('ticketCorrections') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ ticketCorrectionsTitleString }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-6" v-if="corrections.length > 0">
                  <v-col>
                    <span class="title">{{ $t('logTicketCorrections') }}</span>
                    <SettlementCorrections
                    :corrections="corrections"
                    :selectedItems.sync="selectedCorrections"
                    showSelect
                    :contractMode="logsContractMode"/>
                  </v-col>
                </v-row>
                <v-row class="mt-6" v-if="byproductCorrections.length > 0">
                  <v-col>
                    <span class="title">{{ $t('byproductCorrections') }}</span>
                    <SettlementCorrections
                    :corrections="byproductCorrections"
                    :selectedItems.sync="selectedByproductCorrections"
                    showSelect
                    :contractMode="byproductContractMode"/>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header
                active-class="primary white--text"
                hide-actions
                :disabled="totalMiscPayments === 0"
                :style="`cursor: ${totalMiscPayments === 0 ? 'default' : 'pointer'};${totalMiscPayments === 0 ? 'background-color: #f7f7f7' : ''}`">
                <v-row>
                  <v-col>
                    {{ $t('miscPayments') }}
                  </v-col>
                  <v-col cols="auto">
                    {{ miscPaymentsTitleString }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-6" v-if="advances.length > 0">
                  <v-col>
                    <span class="title">{{ $t('advances') }}</span>
                    <ExportBatchSelectionTable
                    :items="advances"
                    :selectedItems.sync="selectedAdvances"
                    :headers="headers.advanceHeaders"
                    tableKey="advanceId"/>
                  </v-col>
                </v-row>
                <v-row class="mt-6" v-if="tractPayments.length > 0">
                  <v-col>
                    <span class="title">{{ $t('tractPayments') }}</span>
                    <ExportBatchSelectionTable
                    :items="tractPayments"
                    :selectedItems.sync="selectedTractPayments"
                    :headers="headers.tractPaymentHeaders"
                    tableKey="tractPayableId"/>
                  </v-col>
                </v-row>
                <v-row class="mt-6" v-if="accountPayments.length > 0">
                  <v-col>
                    <span class="title">{{ $t('accountPayments') }}</span>
                    <ExportBatchSelectionTable
                    :items="accountPayments"
                    :selectedItems.sync="selectedAccountPayments"
                    :headers="headers.accountPaymentHeaders"
                    tableKey="accountPayableId"
                    :customCells="[{ slotName: 'note', value: 'note' }, { slotName: 'actions', value: 'actions' }]">
                      <template #note="{item}">
                        <span>
                          {{ item.note }}
                          <Icon
                          v-if="item.accountPayableTemplateId"
                          icon="mdi-sync"
                          :tooltipText="$t('recurring')"
                          iconColor="black"
                          tooltipColor="black"/>
                        </span>
                      </template>
                      <template #actions="{item}">
                        <Icon
                        :disabled="!userAssignedClaim(UserClaims.AccountManager)"
                        icon="mdi-delete-forever"
                        iconColor="error"
                        :tooltipText="$t('delete')"
                        @icon-clicked="openDeleteAccountPayableConfirmation(item)"/>
                      </template>
                    </ExportBatchSelectionTable>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-row align="center" justify="center" v-else>
            <v-col cols="auto">
              <span class="headline black--text">{{ this.noDataText }}</span>
            </v-col>
          </v-row>
        </v-row>
        <v-row v-if="showSettlementMessage && !noData" class="mt-6 px-2">
          <v-card outlined width="100%">
            <v-card-title class="subtitle-1 pb-2">
              <span>{{ $t('settlementMessage') }}</span>
              <Icon
              margin="ml-2"
              icon="mdi-pencil"
              :tooltipText="$t('editSettlementMessage')"
              data-testid="export-batch-edit-settlement-message"
              @icon-clicked="openSettlementMessageDialog"/>
            </v-card-title>
            <v-card-text class="pt-0">
              <span
              class="body-2 text-pre-wrap"
              data-testid="export-batch-settlement-message-preview">
                {{ settlementMessage || $t('none') }}
              </span>
            </v-card-text>
          </v-card>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-checkbox
      v-model="exportAllFinancials"
      :label="$t('exportAllFinancials')"
      class="mr-2"
      data-testid="export-all-financials-checkbox"
      color="success"
      on-icon="mdi-checkbox-multiple-marked"
      off-icon="mdi-checkbox-multiple-blank-outline"
      @change="exportAllFinancialsChanged"
      :disabled="noData"/>
      <v-btn
      :disabled="!hasSelectedFinancials"
      color="primary"
      @click="openExportBatchDialog">
        {{ $t('createExportBatch') }}
      </v-btn>
    </v-card-actions>
    <v-dialog v-model="confirmExportBatchDialog" width="400px">
      <ConfirmDialog
      :title="$t('createExportBatch')"
      :body="$t('confirmExportBatchCreationMessage')"
      :confirmText="$t('create')"
      color="primary"
      @confirm="save"
      @cancel="closeDialogs"
      />
    </v-dialog>
    <v-dialog v-model="deleteAccountPayableDialog" width="400px">
      <ConfirmDialog
      v-if="deleteAccountPayableDialog"
      :title="$t('deleteAccountPaymentTitle')"
      :body="$t('deleteAccountPaymentBody')"
      @cancel="closeDialogs"
      @confirm="confirmDeleteAccountPayable"/>
    </v-dialog>
    <v-dialog v-model="settlementMessageDialog" width="500" persistent>
      <v-card v-if="settlementMessageDialog">
        <v-card-title class="primary headline white--text">
          <span>{{ $t('editSettlementMessage') }}</span>
          <v-spacer/>
          <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('cancel')"
          @icon-clicked="closeSettlementMessageDialog"/>
        </v-card-title>
        <v-card-text class="mt-6">
          <FormWrapper
          @submit="saveSettlementMessage"
          :buttonText="$t('done')"
          strongValidation>
            <v-textarea
            v-model="settlementMessageDraft"
            :label="$t('settlementMessage')"
            :hint="$t('settlementMessageHint')"
            :rules="[rules.required]"
            outlined
            persistent-hint
            counter
            maxlength="512"
            rows="4"
            auto-grow
            autofocus/>
          </FormWrapper>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ResourceLoader } from '@/utils/ResourceLoader'
import fieldRules from '@/utils/rules.js'
import { settlementTractPaymentHeaders, settlementAccountPaymentHeaders, settlementAdvanceHeaders, ticketCorrectionHeaders } from '@/headers/ExportBatch.js'
import TicketHeaders from '@/headers/Ticket.js'
import { PayPeriodStatus, ContractMode, UserClaims, SettlementEmailMessageMode } from '@/utils/Enumerations.js'
import { ticketFiltersForExport } from '@/utils/TicketFiltering'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { sanitizeSettlementMessage, containsUrl } from '@/utils/SettlementMessageSanitizer.js'

const PERIOD_EXPORT_LABEL = 'Period Export'

export default {
  name: 'ExportBatchDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ExportBatchSelectionTable: () => import('@/components/settlements/export-batch-form/ExportBatchSelectionTable.vue'),
    SettlementCorrections: () => import('@/components/settlements/accounts-payable-module/other-modules/SettlementCorrections.vue'),
    Loading: () => import('@/components/core/Loading.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  data: () => ({
    ticketFiltersForExport,
    UserClaims,
    openPanel: undefined,
    loader: ResourceLoader.empty,
    tickets: [],
    transferTickets: [],
    logYardSaleTickets: [],
    byproductTickets: [],
    tractPayments: [],
    accountPayments: [],
    advances: [],
    corrections: [],
    byproductCorrections: [],
    exportAllFinancials: false,
    confirmExportBatchDialog: false,
    label: '',
    settlementMessage: '',
    rules: fieldRules.rules,
    selectedTickets: [],
    selectedTractPayments: [],
    selectedAccountPayments: [],
    selectedByproductTickets: [],
    selectedTransferTickets: [],
    selectedLogYardSaleTickets: [],
    selectedAdvances: [],
    selectedCorrections: [],
    selectedByproductCorrections: [],
    deleteAccountPayableDialog: false,
    focusedAccountPayable: undefined,
    settlementMessageDialog: false,
    settlementMessageDraft: ''
  }),

  async mounted () {
    this.loader = new ResourceLoader(async () => {
      await this.refreshData()
      this.prefillSettlementMessage()
      if (this.$refs.exportBatchLabelTextfield) this.$refs.exportBatchLabelTextfield.focus()
    })
    await this.loader.load()
  },

  watch: {
    everythingIsSelected (val) {
      this.exportAllFinancials = val
    },

    isLoading (val) {
      this.$nextTick(_ => {
        if (!val && this.$refs.exportBatchLabelTextfield) this.$refs.exportBatchLabelTextfield.focus()
      })
    }
  },

  computed: {
    ...mapGetters('settlements', ['dateConfiguration']),
    ...mapGetters('user', ['companyInfo']),

    showSettlementMessage () {
      return this.companyInfo?.settlementEmailMessageMode === SettlementEmailMessageMode.Default.value
    },

    headers () {
      return {
        tractPaymentHeaders: settlementTractPaymentHeaders().getColumns(),
        advanceHeaders: settlementAdvanceHeaders(true).getColumns(),
        ticketHeaders: TicketHeaders.ticketUnpostingHeaders(this.$i18n.locale),
        byproductTicketHeaders: TicketHeaders.ticketByproductUnpostingHeaders(this.$i18n.locale),
        correctionHeaders: ticketCorrectionHeaders(),
        accountPaymentHeaders: settlementAccountPaymentHeaders().getColumns()
      }
    },

    logsContractMode () {
      return ContractMode.Logs
    },

    byproductContractMode () {
      return ContractMode.Byproducts
    },

    hasSelectedFinancials () {
      const anythingSelected = this.selectedTickets.length > 0 ||
        this.selectedByproductTickets.length > 0 ||
        this.selectedTractPayments.length > 0 ||
        this.selectedAdvances.length > 0 ||
        this.selectedCorrections.length > 0 ||
        this.selectedByproductCorrections.length > 0 ||
        this.selectedTransferTickets.length > 0 ||
        this.selectedLogYardSaleTickets.length > 0 ||
        this.selectedAccountPayments.length > 0

      return (this.exportAllFinancials && !this.noDataText) || anythingSelected
    },

    labelRules () {
      return [this.rules.required, this.rules.doesNotInclude([PERIOD_EXPORT_LABEL], 'periodExportNameError')]
    },

    isValidName () {
      return this.label.trim().length > 0 && this.label.trim() !== PERIOD_EXPORT_LABEL
    },

    noData () {
      const hasAnyFinancials = this.tickets.length > 0 ||
        this.byproductTickets.length > 0 ||
        this.tractPayments.length > 0 ||
        this.advances.length > 0 ||
        this.corrections.length > 0 ||
        this.byproductCorrections.length > 0 ||
        this.transferTickets.length > 0 ||
        this.logYardSaleTickets.length > 0 ||
        this.accountPayments.length > 0

      return !hasAnyFinancials
    },

    noDataText () {
      return this.dateConfiguration.payPeriod.status === PayPeriodStatus.Exported.value
        ? this.$t('payPeriodAlreadyExported')
        : this.$t('noFinancialsAvailableToExport')
    },

    totalCorrections () {
      return this.corrections.length + this.byproductCorrections.length
    },

    totalMiscPayments () {
      return this.tractPayments.length + this.accountPayments.length + this.advances.length
    },

    miscPaymentsTitleString () {
      const selected = this.selectedTractPayments.length + this.selectedAccountPayments.length + this.selectedAdvances.length
      const total = this.totalMiscPayments
      return this.$t('xySelected', { x: selected, y: total })
    },

    ticketCorrectionsTitleString () {
      const selected = this.selectedCorrections.length + this.selectedByproductCorrections.length
      const total = this.totalCorrections
      return this.$t('xySelected', { x: selected, y: total })
    },

    everythingIsSelected () {
      return this.selectedTickets.length === this.tickets.length &&
        this.selectedByproductTickets.length === this.byproductTickets.length &&
        this.selectedTractPayments.length === this.tractPayments.length &&
        this.selectedAdvances.length === this.advances.length &&
        this.selectedCorrections.length === this.corrections.length &&
        this.selectedByproductCorrections.length === this.byproductCorrections.length &&
        this.selectedTransferTickets.length === this.transferTickets.length &&
        this.selectedLogYardSaleTickets.length === this.logYardSaleTickets.length &&
        this.selectedAccountPayments.length === this.accountPayments.length
    },

    isLoading () {
      return this.loader.loading
    }
  },

  methods: {
    userAssignedClaim,
    ...mapActions('export-batch', ['getItemsForExporting', 'createExportBatch']),
    ...mapActions('pay-period', ['exportPeriod']),
    ...mapActions('account', ['deleteAccountPayable']),
    ...mapActions('settlements', ['clearStaleSubModuleData', 'refreshModuleData']),
    async refreshData () {
      const {
        tickets,
        tractPayments,
        advances,
        byproductTickets,
        transferTickets,
        logYardSaleTickets,
        corrections,
        byproductCorrections,
        accountPayments
      } = await this.getItemsForExporting({
        payPeriodId: this.dateConfiguration.payPeriod.payPeriodId,
        fiscalYearId: this.dateConfiguration.fiscalYear.fiscalYearId
      })

      this.tickets = tickets
      this.byproductTickets = byproductTickets
      this.transferTickets = transferTickets
      this.logYardSaleTickets = logYardSaleTickets
      this.tractPayments = tractPayments
      this.advances = advances
      this.corrections = corrections
      this.byproductCorrections = byproductCorrections
      this.accountPayments = accountPayments
    },

    getTitleString (selected, allItems) {
      return this.$t('xySelected', { x: selected.length, y: allItems.length })
    },

    prefillSettlementMessage () {
      if (!this.showSettlementMessage || this.settlementMessage) return
      this.settlementMessage = this.companyInfo?.defaultSettlementMessage ?? ''
    },

    openExportBatchDialog () {
      if (this.label.trim().length === 0) {
        this.setSnackError(this.$t('exportBatchLabelRequired'))
        this.$refs.exportBatchLabelTextfield.focus()
        return
      }

      this.confirmExportBatchDialog = true
    },

    openSettlementMessageDialog () {
      this.settlementMessageDraft = this.settlementMessage
      this.settlementMessageDialog = true
    },

    closeSettlementMessageDialog () {
      this.settlementMessageDialog = false
      this.settlementMessageDraft = ''
    },

    saveSettlementMessage () {
      const sanitized = sanitizeSettlementMessage(this.settlementMessageDraft)
      if (containsUrl(sanitized)) {
        this.setSnackError(this.$t('settlementMessageNoUrls'))
        return
      }
      this.settlementMessage = sanitized
      this.closeSettlementMessageDialog()
    },

    async save () {
      if (this.hasSelectedFinancials === false) {
        this.setSnackError(this.$t('noFinancialItemsSelectedForExportBatch'))
        return
      }

      if (this.isValidName === false) {
        this.setSnackError(this.$t('periodExportNameError'))
        this.$refs.exportBatchLabelTextfield.focus()
        return
      }

      const settlementMessage = this.showSettlementMessage ? sanitizeSettlementMessage(this.settlementMessage) : ''
      const exportBatchCreateRequest = this.exportAllFinancials === true
        ? {
          label: this.label,
          settlementMessage,
          ticketIds: [
            ...this.tickets.map(t => t.ticketId),
            ...this.byproductTickets.map(t => t.ticketId),
            ...this.transferTickets.map(t => t.ticketId),
            ...this.logYardSaleTickets.map(t => t.ticketId)
          ],
          tractPaymentIds: this.tractPayments.map(t => t.tractPayableId),
          advanceIds: this.advances.map(t => t.advanceId),
          ticketCorrectionIds: [
            ...this.corrections.map(t => t.ticketCorrectionId),
            ...this.byproductCorrections.map(t => t.ticketCorrectionId)
          ],
          accountPaymentIds: this.accountPayments.map(ap => ap.accountPayableId)
        }
        : {
          label: this.label,
          settlementMessage,
          ticketIds: [
            ...this.selectedTickets.map(t => t.ticketId),
            ...this.selectedByproductTickets.map(t => t.ticketId),
            ...this.selectedTransferTickets.map(t => t.ticketId),
            ...this.selectedLogYardSaleTickets.map(t => t.ticketId)
          ],
          tractPaymentIds: this.selectedTractPayments.map(t => t.tractPayableId),
          advanceIds: this.selectedAdvances.map(t => t.advanceId),
          ticketCorrectionIds: [
            ...this.selectedCorrections.map(t => t.ticketCorrectionId),
            ...this.selectedByproductCorrections.map(t => t.ticketCorrectionId)
          ],
          accountPaymentIds: this.selectedAccountPayments.map(ap => ap.accountPayableId)
        }

      await this.createExportBatch({ exportBatch: exportBatchCreateRequest, payPeriodId: this.dateConfiguration.payPeriod.payPeriodId })
      this.$emit('close', true)
    },

    openDeleteAccountPayableConfirmation (item) {
      this.focusedAccountPayable = item
      this.deleteAccountPayableDialog = true
    },

    closeDialogs () {
      this.focusedAccountPayable = undefined
      this.deleteAccountPayableDialog = false
      this.confirmExportBatchDialog = false
    },

    async confirmDeleteAccountPayable () {
      await this.deleteAccountPayable({
        accountId: this.focusedAccountPayable.accountId,
        accountPayableId: this.focusedAccountPayable.accountPayableId
      })
      this.closeDialogs()
      await this.refreshData()
      await this.refreshModuleData(false)
      this.clearStaleSubModuleData({ isByproduct: false })
    },

    operateOnEverything (isSelecting) {
      this.selectedTickets = isSelecting ? this.tickets : []
      this.selectedByproductTickets = isSelecting ? this.byproductTickets : []
      this.selectedTransferTickets = isSelecting ? this.transferTickets : []
      this.selectedLogYardSaleTickets = isSelecting ? this.logYardSaleTickets : []
      this.selectedTractPayments = isSelecting ? this.tractPayments : []
      this.selectedAdvances = isSelecting ? this.advances : []
      this.selectedCorrections = isSelecting ? this.corrections : []
      this.selectedByproductCorrections = isSelecting ? this.byproductCorrections : []
      this.selectedAccountPayments = isSelecting ? this.accountPayments : []
    },

    exportAllFinancialsChanged (val) {
      this.operateOnEverything(val)
    }
  }
}
</script>
